import { useState, useEffect } from 'react';
import { motion} from 'motion/react';
import { Mail, Linkedin, Instagram, MessageCircle } from 'lucide-react';
import { Card } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface TeamMember {
  name: string;
  role: string;
  image: string;
  bio: string;
  email?: string;
  linkedin?: string;
  instagram?: string;
  whatsapp?: string;
}

interface ThreeDTeamCarouselProps {
  teamMembers: TeamMember[];
}

export function ThreeDTeamCarousel({ teamMembers }: ThreeDTeamCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-rotate every 4 seconds (pause when hovered)
  useEffect(() => {
    if (isHovered) return; // Don't auto-rotate when hovering
    
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % teamMembers.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [teamMembers.length, isHovered]);

  const getCardPosition = (index: number) => {
    const totalCards = teamMembers.length;
    const center = activeIndex;
    let position = index - center;

    // Normalize position to be within reasonable range
    if (position > totalCards / 2) {
      position = position - totalCards;
    } else if (position < -totalCards / 2) {
      position = position + totalCards;
    }

    return position;
  };

  const getCardStyle = (position: number) => {
    const baseTranslateZ = -200;
    
    if (position === 0) {
      // Active card - center front
      return {
        transform: `translateX(0%) translateZ(100px) rotateY(0deg) scale(1.1)`,
        zIndex: 10,
        opacity: 1,
      };
    } else if (position === 1 || position === -1) {
      // Adjacent cards - partially visible
      const side = position > 0 ? 1 : -1;
      return {
        transform: `translateX(${side * 60}%) translateZ(${baseTranslateZ}px) rotateY(${-side * 25}deg) scale(0.85)`,
        zIndex: 5,
        opacity: 0.7,
      };
    } else if (position === 2 || position === -2) {
      // Second level cards - more hidden
      const side = position > 0 ? 1 : -1;
      return {
        transform: `translateX(${side * 80}%) translateZ(${baseTranslateZ - 100}px) rotateY(${-side * 45}deg) scale(0.7)`,
        zIndex: 3,
        opacity: 0.4,
      };
    } else {
      // Completely hidden cards
      const side = position > 0 ? 1 : -1;
      return {
        transform: `translateX(${side * 100}%) translateZ(${baseTranslateZ - 200}px) rotateY(${-side * 60}deg) scale(0.6)`,
        zIndex: 1,
        opacity: 0.2,
      };
    }
  };

  return (
    <div className="relative w-full h-[550px] md:h-[550px] sm:h-[500px] overflow-hidden perspective-1000 px-0 sm:px-0 md:px-0">
      {/* 3D Container */}
      <div className="relative w-full h-full flex items-center justify-center" style={{ transformStyle: 'preserve-3d' }}>
        {teamMembers.map((member, index) => {
          const position = getCardPosition(index);
          const style = getCardStyle(position);
          const isActive = position === 0;

          return (
            <motion.div
              key={index}
              className="absolute"
              style={{
                transformStyle: 'preserve-3d',
                ...style,
              }}
              animate={style}
              transition={{
                duration: 0.8,
                ease: [0.23, 1, 0.32, 1], // Custom cubic bezier for smooth animation
              }}
              onClick={() => setActiveIndex(index)}
            >
              <Card 
                className={`
                  w-80 md:w-80 sm:w-72 xs:w-64 h-[380px] md:h-[380px] sm:h-[360px] sm:mx-1 cursor-pointer border overflow-hidden
                  ${isActive 
                    ? 'border-primary/50 shadow-2xl shadow-primary/20 glass-strong' 
                    : 'border-border/30 hover:border-primary/30 glass'
                  }
                `}
                style={{
                  background: isActive 
                    ? 'rgba(10, 10, 10, 0.85)'
                    : 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: isActive 
                    ? '0 0 30px rgba(0, 207, 255, 0.15), 0 0 60px rgba(138, 63, 252, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    : '0 0 10px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.8s cubic-bezier(0.23, 1, 0.32, 1), background-color 0.8s ease-out, box-shadow 0.8s ease-out, border-color 0.8s ease-out',
                  willChange: 'background-color, box-shadow, border-color, transform',
                }}
              >
                <div 
                  className="p-5 md:p-6 sm:p-4 h-full flex flex-col relative"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onClick={(e) => {
                    if (isActive) {
                      e.stopPropagation();
                      setActiveIndex((prevIndex) => (prevIndex + 1) % teamMembers.length);
                    }
                  }}
                >
                  {/* Profile Image */}
                  <motion.div
                    className="relative mb-6 md:mb-6 sm:mb-4 self-center"
                    whileHover={isActive ? { scale: 1.05 } : {}}
                    transition={{ duration: 0.3 }}
                  >
                    <div 
                      className={`
                        w-24 h-24 md:w-24 md:h-24 sm:w-20 sm:h-20 rounded-full overflow-hidden border-2
                        ${isActive ? 'border-white/80' : 'border-transparent'}
                      `}
                      style={{
                        transition: 'border-color 0.8s ease-out',
                      }}
                    >
                      <ImageWithFallback
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Glow effect for active card */}
                    {isActive && (
                      <motion.div
                        className="absolute inset-0 rounded-full bg-white/30 -z-10"
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.3, 0.6, 0.3],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                    )}
                  </motion.div>
                  
                  {/* Content */}
                  <div className="text-center flex-1 flex flex-col">
                    <motion.h3 
                      className={`
                        font-bold mb-2
                        ${isActive ? 'text-white text-xl md:text-xl sm:text-lg drop-shadow-md' : 'text-white text-lg md:text-lg sm:text-base'}
                      `}
                      animate={isActive ? { scale: 1.05 } : { scale: 1 }}
                      transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                      style={{
                        transition: 'color 0.8s ease-out, font-size 0.8s ease-out, text-shadow 0.8s ease-out',
                      }}
                    >
                      {member.name}
                    </motion.h3>
                    
                    <p 
                      className={`
                        text-sm md:text-sm sm:text-xs mb-4 md:mb-4 sm:mb-3 font-medium text-center
                        ${isActive ? 'text-primary/90 font-semibold' : 'text-accent/70'}
                      `}
                      style={{
                        transition: 'color 0.8s ease-out, font-weight 0.8s ease-out',
                      }}
                    >
                      {member.role}
                    </p>
                    
                    <p 
                      className={`
                        text-sm md:text-sm sm:text-xs leading-relaxed text-justify flex-1
                        ${isActive 
                          ? 'text-white opacity-100 font-medium' 
                          : 'text-muted-foreground/60 opacity-80'
                        }
                      `}
                      style={{
                        transition: 'color 0.8s ease-out, opacity 0.8s ease-out, font-weight 0.8s ease-out',
                      }}
                    >
                      {member.bio}
                    </p>
                    
                    {/* Social Media Icons - Only show on active card */}
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="flex items-center justify-center gap-2 pt-3 border-t border-white/10 mt-auto"
                      >
                        {member.email && (
                          <a
                            href={`mailto:${member.email}`}
                            className="group relative"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Email"
                          >
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center group-hover:from-primary/30 group-hover:to-primary/20 group-hover:border-primary/50 transition-all duration-300">
                              <Mail className="w-4 h-4 text-primary group-hover:scale-110 transition-transform duration-300" />
                            </div>
                          </a>
                        )}
                        {member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="LinkedIn"
                          >
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center group-hover:from-primary/30 group-hover:to-primary/20 group-hover:border-primary/50 transition-all duration-300">
                              <Linkedin className="w-4 h-4 text-primary group-hover:scale-110 transition-transform duration-300" />
                            </div>
                          </a>
                        )}
                        {member.instagram && (
                          <a
                            href={member.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Instagram"
                          >
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-accent/20 to-accent/10 border border-accent/30 flex items-center justify-center group-hover:from-accent/30 group-hover:to-accent/20 group-hover:border-accent/50 transition-all duration-300">
                              <Instagram className="w-4 h-4 text-accent group-hover:scale-110 transition-transform duration-300" />
                            </div>
                          </a>
                        )}
                        {member.whatsapp && (
                          <a
                            href={`https://wa.me/${member.whatsapp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="WhatsApp"
                          >
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-secondary/20 to-secondary/10 border border-secondary/30 flex items-center justify-center group-hover:from-secondary/30 group-hover:to-secondary/20 group-hover:border-secondary/50 transition-all duration-300">
                              <MessageCircle className="w-4 h-4 text-secondary group-hover:scale-110 transition-transform duration-300" />
                            </div>
                          </a>
                        )}
                      </motion.div>
                    )} 
                  </div>
                </div>

                {/* Card Background Glow for Active */}
                {isActive && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 rounded-lg -z-10"
                    animate={{
                      opacity: [0.2, 0.4, 0.2],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Indicators */}
      <div className="absolute bottom-8 md:bottom-8 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {teamMembers.map((_, index) => (
          <motion.button
            key={index}
            className={`
              w-3 h-3 md:w-3 md:h-3 sm:w-2 sm:h-2 rounded-full transition-all duration-300
              ${index === activeIndex 
                ? 'bg-primary shadow-lg shadow-primary/50' 
                : 'bg-white/30 hover:bg-white/50'
              }
            `}
            onClick={() => setActiveIndex(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </div>

      {/* Navigation Instructions */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute top-4 md:top-4 sm:top-2 left-1/2 transform -translate-x-1/2 text-center px-2"
      >
        <p className="text-sm md:text-sm sm:text-xs text-muted-foreground">
          <span className="hidden md:inline">Click any card or use indicators to navigate • Auto-rotates every 4 seconds</span>
          <span className="md:hidden">Tap cards or indicators • Auto-rotates</span>
        </p>
      </motion.div> */}
    </div>
  );
}